/**
 * #981 stage 2 — one connection to a large CSV's fragment or session room (spec §8.1): the file's
 * collab room plus `?frag=<id|_session>&base=<hash>`, on the same `/ws/collab` upgrade as
 * `useYjs` (whose wire protocol it speaks — the subset a csv room uses). Imperative, not a hook:
 * the grid holds a changing set of fragment rooms (the blocks in view), which hooks can't.
 *
 * - The server's epoch comes first; a CHANGED epoch (a new generation: a discard, a save's seal)
 *   means the room was re-created — the local doc is replaced with a fresh one (`onDocReplaced`).
 * - Close codes that end the room (`4001` reload, `4404` not ready, `4409` stale base, `4403`,
 *   `4410`, `4414` too large to edit on this server) are reported (`onEnd`) and never reconnected; any other drop reconnects with backoff.
 * - ACKNOWLEDGED EDITS. A local update is "acknowledged" once a round trip sent after it came back
 *   (a SYNC_STEP1 answered with SYNC_STEP2: the server handles one socket's frames in order, so its
 *   answer means every earlier update was applied — or refused, which it says with `edit_refused`).
 *   A round trip runs on its own shortly after local edits (`roundTrip` also on demand: a Save waits
 *   for it). Local updates still unacknowledged when the doc is replaced (a new epoch) or the room
 *   ends — or is closed by the caller — are reported (`onLocalEditsLost`) — never dropped silently.
 */
import * as Y from 'yjs'
import * as encoding from 'lib0/encoding'
import * as decoding from 'lib0/decoding'
import { armConnectTimeout, createReconnectController } from '@papan/reconnect'
import { collabWsUrl } from './useYjs.js'

// Must match the server's protocol constants (yjs-handler.ts).
export const MSG_SYNC = 0
export const MSG_SAVE = 2
export const MSG_ROOM_EPOCH = 3
export const MSG_DISCARD = 5
export const MSG_CONFLICT = 8
export const MSG_FORCE_SAVE = 10
/** #1315 A: the file's state ('being-deleted', 'access-lowered', 'read-only' or '' — as useYjs reads it). */
export const MSG_FILE_STATE = 14
export const MSG_CSV_STATE = 15
export const SYNC_STEP1 = 0
export const SYNC_STEP2 = 1
export const SYNC_UPDATE = 2

/** A round trip this long after local edits acknowledges them. */
const AUTO_ACK_MS = 250

/** Close codes after which the room is not reconnected: the session decides what follows. */
export const CSV_END_CLOSE_CODES = new Set([4001, 4404, 4409, 4403, 4410, 4413, 4414])

/** MSG_CSV_STATE's payload (the server's `CsvClientState`, rooms.ts). */
export interface CsvStateMessage {
  state: 'saving' | 'reload' | 'discard_failed' | 'conflicted' | 'resolved' | 'save_retry' | string
  reason?: string
  /** A `reload` after a save: the file's new base. */
  base?: string
}

export interface CsvRoomOptions {
  /** `${tenantId}:${fileId}` — the file's room. */
  room: string
  frag: string
  base: string
  shareToken?: string | null
  onSynced?: () => void
  /** A remote update was applied (`server` origin). */
  onRemoteUpdate?: () => void
  /** The room was re-created (new epoch): `doc` is a fresh one. */
  onDocReplaced?: (doc: Y.Doc) => void
  onState?: (msg: CsvStateMessage) => void
  /** MSG_SAVE: the save's outcome (`reason` only to the client that pressed Save). */
  onSaveReply?: (ok: boolean, reason: string) => void
  onConflict?: (conflicted: boolean) => void
  /** The room ended (a close code in {@link CSV_END_CLOSE_CODES}). */
  onEnd?: (code: number, reason: string) => void
  /** Local edits not yet acknowledged were dropped (the doc replaced, or the room ended). Called before `onEnd`. */
  onLocalEditsLost?: () => void
  /** #1315 A: a MSG_FILE_STATE frame's state (the server's join answer, or a live access / delete change). */
  onFileState?: (state: string) => void
  /**
   * #1315 A: the server's epoch arrived — `reconnected`: this connection already had one (a re-connection, e.g. the
   * 4002 rejoin of a raise), false on its first. Called BEFORE this client answers anything on the new socket (the
   * join's file states and the server's SYNC_STEP1 come after it), so closing the room here replays nothing.
   */
  onEpoch?: (reconnected: boolean) => void
  /** The socket dropped (any close this side did not ask for): an answer to what was sent on it may never come. */
  onDropped?: () => void
}

export interface CsvRoomConnection {
  readonly frag: string
  readonly doc: Y.Doc
  readonly synced: boolean
  /** Resolves true on the first sync, false if the room ends or is closed first. */
  whenSynced(): Promise<boolean>
  /** Sends a bare message type, or one with a string payload. False when not connected. */
  send(msgType: number, payload?: string): boolean
  /** Local updates the server has not acknowledged yet (sent or not). */
  hasUnacked(): boolean
  /** One round trip: true once every local update made before it is acknowledged; false when not connected / closed first. */
  roundTrip(): Promise<boolean>
  close(): void
}

export type CsvRoomConnector = (opts: CsvRoomOptions) => CsvRoomConnection

export function parseCsvState(raw: string): CsvStateMessage | null {
  try {
    const m = JSON.parse(raw) as CsvStateMessage
    return m && typeof m.state === 'string' ? m : null
  } catch {
    return null
  }
}

export const connectCsvRoom: CsvRoomConnector = (opts) => {
  let doc = new Y.Doc()
  let ws: WebSocket | null = null
  let epoch: string | null = null
  let synced = false
  let ended = false
  let ping: ReturnType<typeof setInterval> | null = null
  let clearConnectTimeout: (() => void) | null = null
  let syncWaiters: ((ok: boolean) => void)[] = []
  const settleSync = (ok: boolean) => {
    const w = syncWaiters
    syncWaiters = []
    for (const f of w) f(ok)
  }
  // Acknowledgement of local edits: `localSeq` counts local updates, `ackedSeq` the last one a round trip covered.
  let localSeq = 0
  let ackedSeq = 0
  let ackWaiters: { seq: number; resolve: (ok: boolean) => void }[] = []
  let ackTimer: ReturnType<typeof setTimeout> | null = null
  const settleAcks = (ok: boolean) => {
    const w = ackWaiters
    ackWaiters = []
    for (const a of w) a.resolve(ok)
  }
  const reportLost = () => {
    if (localSeq <= ackedSeq) return
    ackedSeq = localSeq // said once
    opts.onLocalEditsLost?.()
  }
  const reconnect = createReconnectController({ jitter: 0.25 })

  const sendRaw = (bytes: Uint8Array): boolean => {
    if (!ws || ws.readyState !== WebSocket.OPEN) return false
    ws.send(bytes)
    return true
  }

  const sendStep1 = () => {
    const enc = encoding.createEncoder()
    encoding.writeVarUint(enc, MSG_SYNC)
    encoding.writeVarUint(enc, SYNC_STEP1)
    encoding.writeVarUint8Array(enc, Y.encodeStateVector(doc))
    return sendRaw(encoding.toUint8Array(enc))
  }
  const roundTrip = (): Promise<boolean> => {
    if (ended || !synced || !ws || ws.readyState !== WebSocket.OPEN) return Promise.resolve(false)
    const seq = localSeq
    return new Promise<boolean>((resolve) => {
      ackWaiters.push({ seq, resolve })
      if (!sendStep1()) { ackWaiters = ackWaiters.filter((a) => a.resolve !== resolve); resolve(false) }
    })
  }
  /** Local edits get their round trip on their own, shortly after (so "unacknowledged" only means in flight). */
  const scheduleAutoAck = () => {
    if (ackTimer || ended) return
    ackTimer = setTimeout(() => {
      ackTimer = null
      if (localSeq > ackedSeq && synced) void roundTrip()
    }, AUTO_ACK_MS)
  }

  const onLocalUpdate = (update: Uint8Array, origin: unknown) => {
    if (origin === 'server') return
    localSeq++
    scheduleAutoAck()
    const e = encoding.createEncoder()
    encoding.writeVarUint(e, MSG_SYNC)
    encoding.writeVarUint(e, SYNC_UPDATE)
    encoding.writeVarUint8Array(e, update)
    sendRaw(encoding.toUint8Array(e))
  }
  doc.on('update', onLocalUpdate)

  const replaceDoc = () => {
    // A new epoch: the room was re-created and this doc's unacknowledged edits go with it.
    reportLost()
    settleAcks(false)
    doc.off('update', onLocalUpdate)
    doc = new Y.Doc()
    doc.on('update', onLocalUpdate)
    synced = false
    opts.onDocReplaced?.(doc)
  }

  const onMessage = (event: MessageEvent) => {
    const d = decoding.createDecoder(new Uint8Array(event.data as ArrayBuffer))
    const type = decoding.readVarUint(d)
    switch (type) {
      case MSG_ROOM_EPOCH: {
        const e = decoding.readVarString(d)
        const reconnected = epoch !== null
        if (reconnected && epoch !== e) replaceDoc()
        epoch = e
        opts.onEpoch?.(reconnected)
        // the owner may have closed the room just now: nothing more goes out (sendRaw finds no socket)
        sendStep1()
        break
      }
      case MSG_SYNC: {
        const sub = decoding.readVarUint(d)
        if (sub === SYNC_STEP1) {
          const sv = decoding.readVarUint8Array(d)
          const enc = encoding.createEncoder()
          encoding.writeVarUint(enc, MSG_SYNC)
          encoding.writeVarUint(enc, SYNC_STEP2)
          encoding.writeVarUint8Array(enc, Y.encodeStateAsUpdate(doc, sv))
          sendRaw(encoding.toUint8Array(enc))
        } else if (sub === SYNC_STEP2 || sub === SYNC_UPDATE) {
          let changed = false
          const mark = () => { changed = true }
          doc.on('update', mark)
          try {
            Y.applyUpdate(doc, decoding.readVarUint8Array(d), 'server')
          } finally {
            doc.off('update', mark)
          }
          if (sub === SYNC_STEP2 && !synced) {
            synced = true
            settleSync(true)
            opts.onSynced?.()
            // edits made while away were sent in answer to the server's STEP1: have them acknowledged
            if (localSeq > ackedSeq) scheduleAutoAck()
          } else {
            if (sub === SYNC_STEP2) {
              // the answer to a round trip: everything sent before it is acknowledged
              const w = ackWaiters.shift()
              if (w) {
                ackedSeq = Math.max(ackedSeq, w.seq)
                w.resolve(true)
              }
            }
            if (changed) opts.onRemoteUpdate?.()
          }
        }
        break
      }
      case MSG_CSV_STATE: {
        const m = parseCsvState(decoding.readVarString(d))
        if (m) opts.onState?.(m)
        break
      }
      case MSG_SAVE: {
        const ok = decoding.hasContent(d) ? decoding.readVarUint(d) === 1 : true
        const reason = decoding.hasContent(d) ? decoding.readVarString(d) : ''
        opts.onSaveReply?.(ok, reason)
        break
      }
      case MSG_CONFLICT:
        opts.onConflict?.(decoding.readVarUint(d) === 1)
        break
      case MSG_FILE_STATE:
        opts.onFileState?.(decoding.readVarString(d))
        break
      default:
        break // awareness, …: not used by the csv grid
    }
  }

  const end = (code: number, reason: string) => {
    ended = true
    settleSync(false)
    settleAcks(false)
    if (ackTimer) { clearTimeout(ackTimer); ackTimer = null }
    reportLost()
    opts.onEnd?.(code, reason)
  }

  function connect() {
    if (ended) return
    const sock = new WebSocket(collabWsUrl(opts.room, opts.shareToken ?? null, { frag: opts.frag, base: opts.base }))
    sock.binaryType = 'arraybuffer'
    ws = sock
    clearConnectTimeout?.()
    clearConnectTimeout = armConnectTimeout(sock)
    sock.onopen = () => {
      clearConnectTimeout?.()
      reconnect.markConnected()
      ping = setInterval(() => { if (sock.readyState === WebSocket.OPEN) sock.send(new Uint8Array([99])) }, 30_000)
    }
    sock.onmessage = (ev) => { if (ws === sock) onMessage(ev) }
    sock.onclose = (ev) => {
      if (ping) { clearInterval(ping); ping = null }
      if (ws !== sock) return
      ws = null
      synced = false
      settleAcks(false) // not acknowledged: a reconnect re-sends them (the same epoch) or reports them lost
      reconnect.onDropped()
      if (ended) return
      opts.onDropped?.()
      if (CSV_END_CLOSE_CODES.has(ev.code)) end(ev.code, ev.reason || '')
      else if (ev.code === 4002) setTimeout(connect, 0)
      else if (!reconnect.schedule(connect)) end(ev.code, 'gave_up')
    }
  }
  connect()

  return {
    frag: opts.frag,
    get doc() { return doc },
    get synced() { return synced },
    whenSynced() {
      if (synced) return Promise.resolve(true)
      if (ended) return Promise.resolve(false)
      return new Promise<boolean>((r) => syncWaiters.push(r))
    },
    send(msgType, payload) {
      const e = encoding.createEncoder()
      encoding.writeVarUint(e, msgType)
      if (payload !== undefined) encoding.writeVarString(e, payload)
      return sendRaw(encoding.toUint8Array(e))
    },
    hasUnacked: () => localSeq > ackedSeq,
    roundTrip,
    close() {
      if (ended) return
      ended = true
      settleSync(false)
      settleAcks(false)
      if (ackTimer) { clearTimeout(ackTimer); ackTimer = null }
      // closed with edits the server never acknowledged: said, never dropped silently
      reportLost()
      reconnect.cancel()
      clearConnectTimeout?.()
      if (ping) { clearInterval(ping); ping = null }
      doc.off('update', onLocalUpdate)
      const s = ws
      ws = null
      s?.close()
    },
  }
}
